import { noteStorageLimit } from "@/shared/billing/storage-limit";
import { fetchWithSession, HttpError, request } from "@/shared/http/client";
import { beginLoading } from "@/shared/http/loading-bar";

/**
 * Images on their way into a turn.
 *
 * The agent takes them as NATIVE model input (ADR-007), not as a tool call — the
 * model sees the picture rather than a description of it written by something
 * that saw it first. So the job here is only to turn a browser `File` into the
 * `{ mediaType, data }` pair the API expects. WHICH files take this road is
 * `routeOf`'s decision, from the server's list — a small PNG, JPEG, GIF or WebP;
 * everything else readable is uploaded and read on the server (`PendingFile`).
 */
export interface PendingImage {
	/** For the thumbnail. Revoked when the image is removed, or it leaks. */
	readonly previewUrl: string;
	readonly name: string;
	readonly mediaType: string;
	/** Base64, WITHOUT the `data:` prefix — that is what the providers want. */
	readonly data: string;
}

/**
 * Why a file was refused — a REASON and not a sentence.
 *
 * The sentence is the screen's: this module has no locale and no way to ask for
 * one, and a phrase built here would be one the panel could not translate. The
 * reason names a key under `attach.*`, and the composer says it with the file's
 * name in it.
 */
export type AttachFailure = {
	kind: "type" | "size" | "upload";
	reason: "badType" | "tooBig" | "uploadFailed";
};

/** Só converte: quem decidiu que este arquivo vai por aqui foi `routeOf`. */
export async function toPendingImage(file: File): Promise<PendingImage> {
	const data = await toBase64(file);
	return {
		previewUrl: URL.createObjectURL(file),
		name: file.name,
		mediaType: file.type,
		data,
	};
}

export function isFailure(value: unknown): value is AttachFailure {
	return typeof value === "object" && value !== null && "reason" in value;
}

/**
 * A nota de voz gravada no compositor, pronta para viajar no turno.
 *
 * O modelo de chat não ouve: o servidor transcreve na borda (`promptWithAudio`)
 * e o turno segue como texto. Aqui só se converte o `File` do gravador no par
 * `{ mediaType, data }` — o mesmo contrato das imagens acima.
 */
export interface PendingAudio {
	readonly mediaType: string;
	/** Base64, sem o prefixo `data:` — como nas imagens. */
	readonly data: string;
	readonly seconds: number;
	/** Para a bolha local TOCAR o que acabou de ser dito. Revogado como as imagens. */
	readonly previewUrl: string;
}

export async function toPendingAudio(file: File, seconds: number): Promise<PendingAudio> {
	return {
		mediaType: file.type,
		data: await toBase64(file),
		seconds,
		previewUrl: URL.createObjectURL(file),
	};
}

/**
 * Os arquivos de um colar ou de um arrastar — TODOS, não só as imagens.
 *
 * Quem decide o que o Ben lê é a lista do servidor (`attach`); filtrar aqui por
 * `image/` era a segunda cópia da regra que deixou o vídeo de fora (25/09).
 */
export function filesFrom(items: DataTransfer | null): File[] {
	if (!items) return [];
	return Array.from(items.files);
}

/**
 * O QUE O BEN LÊ — a lista do servidor (`GET /v3/agents/attachments`), a única.
 *
 * O compositor monta o `accept` do seletor e a frase de recusa daqui, e não de
 * uma lista própria: a lista de quatro tipos que morava neste arquivo recusou um
 * vídeo que o servidor sabia ver e ouvir. Pedida uma vez por aba.
 */
export interface ReadableFormats {
	readonly formats: ReadonlyArray<{
		readonly kind: "image" | "video" | "audio" | "pdf" | "text" | "document";
		readonly label: string;
		readonly extensions: readonly string[];
		readonly mimes: readonly string[];
	}>;
	readonly maxBytes: number;
	readonly maxFiles: number;
	readonly inlineImageBytes: number;
}

let formatsRequest: Promise<ReadableFormats> | null = null;

export function readableFormats(): Promise<ReadableFormats> {
	formatsRequest ??= request<ReadableFormats>("/v3/agents/attachments").catch((e: unknown) => {
		// Falhou? Da próxima vez pede de novo — uma rede que piscou não pode deixar
		// o seletor sem lista até recarregar a aba.
		formatsRequest = null;
		throw e;
	});
	return formatsRequest;
}

/** O `accept` do seletor: os mimes e as extensões da lista. */
export function acceptOf(formats: ReadableFormats): string {
	return formats.formats
		.flatMap((f) => [...f.mimes, ...f.extensions.map((e) => `.${e}`)])
		.join(",");
}

/** "PNG, JPEG, …" — cada nome uma vez, na ordem da lista. */
export function labelsOf(formats: ReadableFormats): string {
	return [...new Set(formats.formats.map((f) => f.label))].join(", ");
}

/** Pelo mime; pela extensão quando o navegador não diz (um .md chega sem tipo). */
function kindOf(
	formats: ReadableFormats,
	file: File,
): ReadableFormats["formats"][number]["kind"] | null {
	const type = file.type.split(";", 1)[0]?.trim().toLowerCase() ?? "";
	const byMime = formats.formats.find((f) => f.mimes.includes(type));
	if (byMime) return byMime.kind;
	const extension = file.name.toLowerCase().split(".").pop() ?? "";
	return formats.formats.find((f) => f.extensions.includes(extension))?.kind ?? null;
}

/**
 * Um anexo que NÃO viaja em base64: vídeo, áudio, PDF, planilha — e a foto
 * grande demais para o corpo do turno. Já está guardado quando o turno sai; o
 * turno leva só o `id`, e o servidor lê (quadros, transcrição, texto).
 */
export interface PendingFile {
	readonly id: string;
	readonly name: string;
	readonly mime: string;
	readonly sizeBytes: number;
}

/** Como o compositor deve tratar este arquivo — decidido pela lista do servidor. */
export function routeOf(formats: ReadableFormats, file: File): "image" | "file" | AttachFailure {
	const kind = kindOf(formats, file);
	if (!kind) return { kind: "type", reason: "badType" };
	if (file.size > formats.maxBytes) return { kind: "size", reason: "tooBig" };
	// A foto pequena viaja em base64 e o modelo vê-a nativamente. A grande sobe
	// como arquivo e o servidor reduz: base64 incha um terço, e o turno inteiro é
	// um corpo JSON só.
	// Só com um mime de imagem da lista: a que chegou sem tipo (reconhecida pela
	// extensão) sobe como arquivo, e o servidor lê o formato pelos bytes.
	const declared = formats.formats.some((f) => f.kind === "image" && f.mimes.includes(file.type));
	return declared && file.size <= formats.inlineImageBytes ? "image" : "file";
}

const API_BASE = import.meta.env.VITE_API_BASE ?? "";
/** Um vídeo de 25 MB por uma rede de telemóvel não sobe em 15 s. */
const UPLOAD_TIMEOUT_MS = 120_000;

/**
 * Sobe o arquivo pela porta de todo upload (`POST /v3/files/upload`), em
 * MULTIPART — o corpo JSON do servidor tem teto de 20 MB, e um vídeo de 15 MB
 * em base64 já passa disso.
 */
export async function uploadPendingFile(file: File): Promise<PendingFile | AttachFailure> {
	const form = new FormData();
	form.append("file", file);
	form.append("ownerKind", "agent");
	form.append("purpose", "attachment");
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
	// O upload não passa por `request` (multipart), então acende a barra do topo
	// por conta própria — é justamente a espera mais longa que alguém faz no painel.
	const endLoading = beginLoading("http");
	let failed = true;
	try {
		// No Content-Type header on purpose: only the browser knows the multipart
		// boundary it just generated, so setting one here breaks the parse.
		const res = await fetchWithSession(`${API_BASE}/v3/files/upload`, {
			method: "POST",
			body: form,
			credentials: "include",
			signal: controller.signal,
		});
		if (!res.ok) {
			const payload = await res.json().catch(() => null);
			const refused = new HttpError(
				"server",
				`upload failed with ${res.status}`,
				res.status,
				undefined,
				payload,
			);
			// Armazenamento cheio: o diálogo da moldura oferece mais; a barra não fica vermelha.
			if (noteStorageLimit(refused)) failed = false;
			throw refused;
		}
		const payload = (await res.json()) as {
			file: { id: string; path: string; mimeType: string; sizeBytes: number };
		};
		failed = false;
		return {
			id: payload.file.id,
			name: file.name,
			mime: payload.file.mimeType,
			sizeBytes: payload.file.sizeBytes,
		};
	} catch {
		return { kind: "upload", reason: "uploadFailed" };
	} finally {
		clearTimeout(timer);
		endLoading(failed);
	}
}

function toBase64(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const result = String(reader.result ?? "");
			// `readAsDataURL` gives `data:image/png;base64,AAAA`. The providers want
			// the payload alone; sending the prefix is a silent 400.
			const comma = result.indexOf(",");
			resolve(comma >= 0 ? result.slice(comma + 1) : result);
		};
		// A code, not a sentence: what the person reads is `attach.unreadable`, said
		// by the composer that has a locale.
		reader.onerror = () => reject(new Error("UNREADABLE_IMAGE"));
		reader.readAsDataURL(file);
	});
}
